import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BillFile } from '../../models';
import { formatBillDate } from '../../utils/date-format.util';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-duplicate-resolver',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './duplicate-resolver.component.html',
  styleUrl: './duplicate-resolver.component.scss'
})
export class DuplicateResolverComponent implements OnInit {
    @Input() newBill!: BillFile;
    @Input() existingBill!: BillFile;
    @Output() resolve = new EventEmitter<'keep' | 'delete'>();

    existingSafeSrc: SafeResourceUrl | null = null;
    newSafeSrc: SafeResourceUrl | null = null;
    matchReasons: string[] = [];
    existingPeriod = '';
    newPeriod = '';

    constructor(private sanitizer: DomSanitizer) {}

    ngOnInit() {
        this.existingSafeSrc = this.getSafePdfUrl(this.existingBill);
        this.newSafeSrc = this.getSafePdfUrl(this.newBill);
        this.matchReasons = this.computeMatchReasons();
        this.existingPeriod = this.existingBill.data?.billingPeriodStart
            ? formatBillDate(this.existingBill.data.billingPeriodStart) : '-';
        this.newPeriod = this.newBill.data?.billingPeriodStart
            ? formatBillDate(this.newBill.data.billingPeriodStart) : '-';
    }

    private getSafePdfUrl(bill: BillFile): SafeResourceUrl | null {
        const url = this.getBlobUrl(bill);
        return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    }

    private getBlobUrl(bill: BillFile): string | null {
        if (!bill) return null;
        if (bill.previewUrl) return bill.previewUrl;
        if (bill.file?.size > 0) {
            bill.previewUrl = URL.createObjectURL(bill.file);
            return bill.previewUrl;
        }
        if (bill.fileData?.length) {
            const blob = new Blob([new Uint8Array(bill.fileData)], { type: 'application/pdf' });
            bill.previewUrl = URL.createObjectURL(blob);
            return bill.previewUrl;
        }
        return null;
    }

    private computeMatchReasons(): string[] {
        const reasons: string[] = [];
        if (this.existingBill?.file.name === this.newBill?.file.name) {
            reasons.push('Same file name');
        }
        const a = this.existingBill?.data;
        const b = this.newBill?.data;
        if (!a || !b) return reasons;
        if (a.utilityAccountNumber && a.utilityAccountNumber === b.utilityAccountNumber) {
            reasons.push('Same account number');
        }
        if (a.billingPeriodStart && a.billingPeriodStart === b.billingPeriodStart) {
            reasons.push('Same billing period');
        }
        if (a.totalAmount != null && a.totalAmount === b.totalAmount) {
            reasons.push('Same amount');
        }
        return reasons;
    }

    onKeep() { this.resolve.emit('keep'); }
    onDelete() { this.resolve.emit('delete'); }
}
